import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Grid, Button, Chip, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tab, Tabs,
  Alert, CircularProgress, IconButton, Tooltip, InputAdornment, Divider, MenuItem,
} from '@mui/material';
import ArrowBackIcon          from '@mui/icons-material/ArrowBack';
import PendingIcon            from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import RestaurantIcon         from '@mui/icons-material/Restaurant';
import DirectionsCarIcon      from '@mui/icons-material/DirectionsCar';
import StorefrontIcon         from '@mui/icons-material/Storefront';
import LocalShippingIcon      from '@mui/icons-material/LocalShipping';
import GroupsIcon             from '@mui/icons-material/Groups';
import SearchIcon             from '@mui/icons-material/Search';
import RefreshIcon            from '@mui/icons-material/Refresh';
import ThumbUpIcon            from '@mui/icons-material/ThumbUp';
import ThumbDownIcon          from '@mui/icons-material/ThumbDown';
import PersonIcon             from '@mui/icons-material/Person';
import BusinessIcon           from '@mui/icons-material/Business';
import PhoneIcon              from '@mui/icons-material/Phone';
import EmailIcon              from '@mui/icons-material/Email';
import LocationOnIcon         from '@mui/icons-material/LocationOn';
import DescriptionIcon        from '@mui/icons-material/Description';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DownloadIcon           from '@mui/icons-material/Download';
import OpenInNewIcon          from '@mui/icons-material/OpenInNew';
import FolderIcon             from '@mui/icons-material/Folder';
import CarRentalIcon          from '@mui/icons-material/CarRental';
import TwoWheelerIcon         from '@mui/icons-material/TwoWheeler';
import CelebrationIcon       from '@mui/icons-material/Celebration';
import BlockIcon             from '@mui/icons-material/Block';
import AssignmentIcon        from '@mui/icons-material/Assignment';
import partnerService from '../../services/partner.service';
import { fmtDateTZ } from '../../utils/date';

// ── Palette (identique AdminDashboard) ──────────────────────────────────────
const P = {
  bg:        '#0d0f14',
  card:      '#141821',
  cardBorder:'rgba(255,255,255,0.06)',
  purple:    '#552583',
  purpleL:   '#7a3aad',
  gold:      '#FDB927',
  goldD:     '#c98a00',
  green:     '#22c55e',
  blue:      '#3b82f6',
  orange:    '#f59e0b',
  red:       '#ef4444',
  text:      '#f1f5f9',
  textSoft:  'rgba(255,255,255,0.55)',
  textMuted: 'rgba(255,255,255,0.3)',
};

// ── Maps ─────────────────────────────────────────────────────────────────────
const TYPE_MAP = {
  restaurant:  { label: 'Restaurant',    icon: <RestaurantIcon   fontSize="small" />, color: P.red },
  vtc_driver:  { label: 'Chauffeur VTC', icon: <DirectionsCarIcon fontSize="small" />, color: P.purpleL },
  store:       { label: 'Magasin',       icon: <StorefrontIcon   fontSize="small" />, color: P.blue },
  logistics:   { label: 'Logistique',    icon: <LocalShippingIcon fontSize="small" />, color: P.orange },
  carpool:     { label: 'Covoiturage',   icon: <GroupsIcon        fontSize="small" />, color: P.gold },
  delivery:    { label: 'Livreur',       icon: <TwoWheelerIcon    fontSize="small" />, color: '#06b6d4' },
  car_rental:  { label: 'Location',      icon: <CarRentalIcon     fontSize="small" />, color: P.blue },
};

const DOC_LABELS = {
  id_card: "Pièce d'identité",
  kbis: 'Extrait Kbis',
  business_license: 'Licence commerciale',
  driver_license: 'Permis de conduire',
  insurance: 'Assurance',
  vehicle_registration: 'Carte grise',
  rib: 'RIB',
};

const getDocCount = (app) => {
  try {
    const docs = typeof app.documents === 'string' ? JSON.parse(app.documents) : app.documents;
    if (!docs || typeof docs !== 'object') return 0;
    return Object.values(docs).filter(Boolean).length;
  } catch { return 0; }
};

const downloadFile = async (url, filename) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(url, '_blank');
  }
};

const STATUS_MAP = {
  pending:  { label: 'En attente', color: P.orange },
  approved: { label: 'Approuvé',   color: P.green },
  rejected: { label: 'Rejeté',     color: P.red },
};

const fmt = (d) => fmtDateTZ(d, { day: 'numeric', month: 'short', year: 'numeric' });

// ── Composant KPI ────────────────────────────────────────────────────────────
const KpiCard = ({ icon, label, value, color, active, onClick }) => (
  <Card onClick={onClick} sx={{
    bgcolor: P.card, border: `1px solid ${active ? color : P.cardBorder}`,
    borderRadius: '16px', cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s',
    '&:hover': onClick ? { border: `1px solid ${color}66`, transform: 'translateY(-2px)', boxShadow: `0 8px 32px ${color}18` } : {},
  }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: 26 }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ color, lineHeight: 1 }}>{value}</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: P.textSoft, mt: 0.4 }}>{label}</Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Info row dans le dialog ───────────────────────────────────────────────────
const InfoRow = ({ icon, label, value }) => (
  <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ py: 1.2, borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
    <Box sx={{ color: P.textMuted, mt: 0.2, flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" sx={{ color: P.textMuted, display: 'block' }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ color: P.text }}>{value || '—'}</Typography>
    </Box>
  </Box>
);

// ════════════════════════════════════════════════════════════════════════════
export default function PartnerVerification() {
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [stats,        setStats]        = useState({ pending: 0, approved: 0, rejected: 0 });
  const [tab,          setTab]          = useState(0);
  const [search,       setSearch]       = useState('');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [selectedApp,  setSelectedApp]  = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // id en cours
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };
  const showError   = (msg) => { setError(msg);   setTimeout(() => setError(''),   4000); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [appsData, statsData] = await Promise.all([
        partnerService.getApplications(),
        partnerService.getStats(),
      ]);
      setApplications(appsData.applications || []);
      setStats(statsData.stats || { pending: 0, approved: 0, rejected: 0 });
    } catch {
      showError('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleApprove = async (appId) => {
    setActionLoading(appId);
    try {
      await partnerService.approve(appId);
      setSelectedApp(null);
      showSuccess('Demande approuvée -- le partenaire peut maintenant utiliser la plateforme');
      loadData();
    } catch (err) {
      showError(err.response?.data?.message || 'Erreur lors de l\'approbation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (appId) => {
    if (!rejectionReason.trim()) { showError('Veuillez indiquer une raison de rejet'); return; }
    setActionLoading(appId);
    try {
      await partnerService.reject(appId, rejectionReason);
      setSelectedApp(null);
      setRejectionReason('');
      showSuccess('Demande rejetée avec la raison fournie');
      loadData();
    } catch (err) {
      showError(err.response?.data?.message || 'Erreur lors du rejet');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Filtrage ───────────────────────────────────────────────────────────────
  const TAB_STATUS = ['pending', 'approved', 'rejected', 'all'];

  const filtered = applications.filter((app) => {
    const status = app.status || app.partner_status;
    if (tab < 3 && status !== TAB_STATUS[tab]) return false;
    if (typeFilter && (app.partner_type || app.type) !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        app.first_name?.toLowerCase().includes(q) ||
        app.last_name?.toLowerCase().includes(q)  ||
        app.email?.toLowerCase().includes(q)       ||
        app.business_name?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const getType  = (app) => app.partner_type || app.type || '';
  const getStatus = (app) => app.status || app.partner_status || 'pending';

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: P.bg }}>
      <Box textAlign="center">
        <CircularProgress sx={{ color: P.gold, mb: 2 }} size={48} />
        <Typography sx={{ color: P.textMuted }}>Chargement des demandes…</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: P.bg, pb: 6 }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ background: `linear-gradient(135deg, #0a0c10 0%, #141821 100%)`, borderBottom: `1px solid ${P.cardBorder}`, py: 3 }}>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/admin')} sx={{ color: P.textSoft, '&:hover': { color: P.gold, bgcolor: 'rgba(253,185,39,0.08)' } }}>
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: `linear-gradient(135deg, ${P.purple}, ${P.gold})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AdminPanelSettingsIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: P.text, lineHeight: 1.1 }}>
                  Vérification{' '}
                  <Box component="span" sx={{ background: `linear-gradient(90deg, ${P.purple}, ${P.gold})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Partenaires
                  </Box>
                </Typography>
                <Typography variant="caption" sx={{ color: P.textMuted }}>
                  Gérer les demandes d'inscription des nouveaux partenaires
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Rafraîchir">
              <IconButton onClick={loadData} sx={{ color: P.textSoft, '&:hover': { color: P.gold } }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3 }}>

        {/* ── Alertes ─────────────────────────────────────────────────────── */}
        {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px', bgcolor: 'rgba(34,197,94,0.1)', color: P.green, border: `1px solid rgba(34,197,94,0.25)` }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <KpiCard icon={<PendingIcon />}      label="En attente" value={stats.pending  || 0} color={P.orange} active={tab === 0} onClick={() => setTab(0)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard icon={<CheckCircleIcon />}  label="Approuvés"  value={stats.approved || 0} color={P.green}  active={tab === 1} onClick={() => setTab(1)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard icon={<CancelIcon />}       label="Rejetés"    value={stats.rejected || 0} color={P.red}    active={tab === 2} onClick={() => setTab(2)} />
          </Grid>
        </Grid>

        {/* ── Filtres ─────────────────────────────────────────────────────── */}
        <Card sx={{ mb: 2.5, bgcolor: P.card, border: `1px solid ${P.cardBorder}`, borderRadius: '16px' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField fullWidth size="small" placeholder="Nom, email, business…"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: P.textMuted, fontSize: 18 }} /></InputAdornment>,
                    sx: { borderRadius: '10px', color: P.text, '& fieldset': { borderColor: P.cardBorder }, '&:hover fieldset': { borderColor: P.gold } } }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select fullWidth size="small" label="Type de partenaire" value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  InputLabelProps={{ sx: { color: P.textMuted } }}
                  SelectProps={{ sx: { color: P.text } }}
                  sx={{ '& fieldset': { borderColor: P.cardBorder }, '& .MuiSvgIcon-root': { color: P.textMuted } }}>
                  <MenuItem value="">Tous les types</MenuItem>
                  {Object.entries(TYPE_MAP).map(([k, v]) => (
                    <MenuItem key={k} value={k}>
                      <Box display="flex" alignItems="center" gap={1}>{v.icon} {v.label}</Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" sx={{ color: P.textMuted, textAlign: 'right' }}>
                  <Box component="span" sx={{ color: P.gold, fontWeight: 700 }}>{filtered.length}</Box> résultat{filtered.length > 1 ? 's' : ''}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <Card sx={{ mb: 2.5, bgcolor: P.card, border: `1px solid ${P.cardBorder}`, borderRadius: '16px', overflow: 'hidden' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{
              px: 1,
              '& .MuiTab-root': { color: P.textMuted, fontWeight: 600, fontSize: '0.82rem', minHeight: 52, textTransform: 'none', '&.Mui-selected': { color: P.gold } },
              '& .MuiTabs-indicator': { bgcolor: P.gold, height: 3, borderRadius: '3px 3px 0 0' },
            }}>
            <Tab label={`En attente · ${stats.pending || 0}`} />
            <Tab label={`Approuvés · ${stats.approved || 0}`} />
            <Tab label={`Rejetés · ${stats.rejected || 0}`} />
            <Tab label="Toutes les demandes" />
          </Tabs>
        </Card>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <Card sx={{ bgcolor: P.card, border: `1px solid ${P.cardBorder}`, borderRadius: '16px', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.02)', '& th': { borderColor: 'rgba(255,255,255,0.05)', color: P.textMuted, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', py: 1.8 } }}>
                  <TableCell>Type</TableCell>
                  <TableCell>Partenaire</TableCell>
                  <TableCell>Business</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="center">Docs</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Statut</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((app) => {
                  const t = TYPE_MAP[getType(app)]   || { label: getType(app),   color: P.textMuted, icon: <StorefrontIcon fontSize="small" /> };
                  const s = STATUS_MAP[getStatus(app)]|| { label: getStatus(app), color: P.textMuted };
                  const isPending = getStatus(app) === 'pending';
                  const isLoading = actionLoading === app.id;
                  return (
                    <TableRow key={app.id} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.04)', py: 1.4 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>

                      {/* Type */}
                      <TableCell>
                        <Chip icon={t.icon} label={t.label} size="small"
                          sx={{ bgcolor: `${t.color}15`, color: t.color, fontWeight: 700, fontSize: '0.72rem',
                            '& .MuiChip-icon': { color: 'inherit', fontSize: '14px !important' } }} />
                      </TableCell>

                      {/* Partenaire */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: `${t.color}18`, color: t.color, fontSize: '0.9rem', fontWeight: 700 }}>
                            {app.first_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ color: P.text }}>{app.first_name} {app.last_name}</Typography>
                            <Typography variant="caption" sx={{ color: P.textMuted }}>{app.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Business */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: P.textSoft }}>{app.business_name || '—'}</Typography>
                        {app.business_address && (
                          <Typography variant="caption" sx={{ color: P.textMuted, display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                            <LocationOnIcon sx={{ fontSize: 11 }} />{app.business_address}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Contact */}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: P.textSoft }}>{app.phone || '—'}</Typography>
                      </TableCell>

                      {/* Docs */}
                      <TableCell align="center">
                        {(() => {
                          const count = getDocCount(app);
                          return count > 0 ? (
                            <Chip
                              icon={<FolderIcon sx={{ fontSize: '14px !important' }} />}
                              label={`${count}`}
                              size="small"
                              onClick={() => { setSelectedApp(app); setRejectionReason(''); }}
                              sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: P.blue, fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer',
                                '& .MuiChip-icon': { color: 'inherit' },
                                '&:hover': { bgcolor: 'rgba(59,130,246,0.2)' } }}
                            />
                          ) : (
                            <Typography sx={{ color: P.textMuted, fontSize: '0.72rem' }}>—</Typography>
                          );
                        })()}
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography variant="caption" sx={{ color: P.textMuted }}>{fmt(app.created_at)}</Typography>
                      </TableCell>

                      {/* Statut */}
                      <TableCell align="center">
                        <Chip label={s.label} size="small"
                          sx={{ bgcolor: `${s.color}18`, color: s.color, fontWeight: 700, fontSize: '0.72rem' }} />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="Voir le dossier">
                            <IconButton size="small" onClick={() => { setSelectedApp(app); setRejectionReason(''); }}
                              sx={{ color: P.blue, '&:hover': { bgcolor: 'rgba(59,130,246,0.1)' } }}>
                              <VisibilityIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          {isPending && (
                            <>
                              <Tooltip title="Approuver rapidement">
                                <IconButton size="small" onClick={() => handleApprove(app.id)} disabled={isLoading}
                                  sx={{ color: P.green, '&:hover': { bgcolor: 'rgba(34,197,94,0.1)' } }}>
                                  {isLoading ? <CircularProgress size={14} sx={{ color: P.green }} /> : <ThumbUpIcon sx={{ fontSize: 17 }} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Voir pour rejeter">
                                <IconButton size="small" onClick={() => { setSelectedApp(app); setRejectionReason(''); }}
                                  sx={{ color: P.red, '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}>
                                  <ThumbDownIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 8, textAlign: 'center' }}>
                      <Box>
                        <Box sx={{ mb: 1 }}>
                          {tab === 0 ? <CelebrationIcon sx={{ fontSize: 48, color: '#552583' }} /> : tab === 1 ? <CheckCircleIcon sx={{ fontSize: 48, color: '#22c55e' }} /> : tab === 2 ? <BlockIcon sx={{ fontSize: 48, color: '#ef4444' }} /> : <AssignmentIcon sx={{ fontSize: 48, color: '#6b6b6b' }} />}
                        </Box>
                        <Typography variant="body1" fontWeight={700} sx={{ color: P.textSoft, mb: 0.5 }}>
                          {tab === 0 ? 'Aucune demande en attente' : tab === 1 ? 'Aucune demande approuvée' : tab === 2 ? 'Aucune demande rejetée' : 'Aucune demande'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: P.textMuted }}>
                          {search || typeFilter ? 'Essayez de modifier vos filtres' : 'Revenez plus tard'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* ════════════════════════════════════════════════════════════════════
          DIALOG DÉTAIL
      ════════════════════════════════════════════════════════════════════ */}
      <Dialog open={!!selectedApp} onClose={() => setSelectedApp(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: P.card, border: `1px solid ${P.cardBorder}`, borderRadius: '20px' } }}>
        {selectedApp && (() => {
          const t  = TYPE_MAP[getType(selectedApp)]    || { label: getType(selectedApp),    color: P.textMuted, icon: <StorefrontIcon /> };
          const s  = STATUS_MAP[getStatus(selectedApp)] || { label: getStatus(selectedApp), color: P.textMuted };
          const isPending  = getStatus(selectedApp) === 'pending';
          const isRejected = getStatus(selectedApp) === 'rejected';
          const isLoading  = actionLoading === selectedApp.id;

          return (
            <>
              {/* Dialog Header */}
              <DialogTitle sx={{ pb: 0 }}>
                <Box sx={{ p: 2.5, mb: 2, borderRadius: '14px', background: `linear-gradient(135deg, ${t.color}12, ${t.color}06)`, border: `1px solid ${t.color}20` }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: `${t.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.color, fontSize: 26 }}>
                      {t.icon}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight={800} sx={{ color: P.text }}>
                        {selectedApp.first_name} {selectedApp.last_name}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        <Chip label={t.label} size="small" sx={{ bgcolor: `${t.color}18`, color: t.color, fontWeight: 700, fontSize: '0.72rem' }} />
                        <Chip label={s.label} size="small" sx={{ bgcolor: `${s.color}18`, color: s.color, fontWeight: 700, fontSize: '0.72rem' }} />
                        <Chip icon={<AccessTimeIcon sx={{ fontSize: '12px !important' }} />} label={fmt(selectedApp.created_at)} size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: P.textMuted, fontSize: '0.68rem' }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </DialogTitle>

              <DialogContent sx={{ pt: 0 }}>
                <Grid container spacing={2.5}>

                  {/* Infos personnelles */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.03)', border: `1px solid ${P.cardBorder}` }}>
                      <Typography variant="overline" sx={{ color: P.gold, fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.68rem', display: 'block', mb: 1 }}>
                        Informations personnelles
                      </Typography>
                      <InfoRow icon={<PersonIcon sx={{ fontSize: 16 }} />}   label="Nom complet" value={`${selectedApp.first_name} ${selectedApp.last_name}`} />
                      <InfoRow icon={<EmailIcon  sx={{ fontSize: 16 }} />}   label="Email"        value={selectedApp.email} />
                      <InfoRow icon={<PhoneIcon  sx={{ fontSize: 16 }} />}   label="Téléphone"    value={selectedApp.phone} />
                    </Box>
                  </Grid>

                  {/* Infos business */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.03)', border: `1px solid ${P.cardBorder}` }}>
                      <Typography variant="overline" sx={{ color: P.blue, fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.68rem', display: 'block', mb: 1 }}>
                        Informations commerciales
                      </Typography>
                      <InfoRow icon={<BusinessIcon    sx={{ fontSize: 16 }} />} label="Nom du commerce" value={selectedApp.business_name} />
                      <InfoRow icon={<LocationOnIcon  sx={{ fontSize: 16 }} />} label="Adresse"          value={selectedApp.business_address} />
                      {selectedApp.description && (
                        <InfoRow icon={<DescriptionIcon sx={{ fontSize: 16 }} />} label="Description" value={selectedApp.description} />
                      )}
                    </Box>
                  </Grid>

                  {/* Documents */}
                  {selectedApp.documents && Object.keys(typeof selectedApp.documents === 'string' ? JSON.parse(selectedApp.documents) : selectedApp.documents).length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.03)', border: `1px solid ${P.cardBorder}` }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Typography variant="overline" sx={{ color: P.orange, fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.68rem' }}>
                            Documents fournis
                          </Typography>
                          <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => {
                              const docs = typeof selectedApp.documents === 'string' ? JSON.parse(selectedApp.documents) : selectedApp.documents;
                              Object.entries(docs).filter(([, v]) => v && typeof v === 'string' && v.startsWith('http')).forEach(([key, url]) => {
                                const ext = url.match(/\.(jpg|jpeg|png|webp|pdf)/i)?.[1] || 'pdf';
                                downloadFile(url, `${selectedApp.first_name}_${selectedApp.last_name}_${key}.${ext}`);
                              });
                            }}
                            sx={{ color: P.orange, textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                              '&:hover': { bgcolor: 'rgba(245,158,11,0.08)' } }}>
                            Tout télécharger
                          </Button>
                        </Box>
                        <Grid container spacing={1.5}>
                          {(() => {
                            const docs = typeof selectedApp.documents === 'string' ? JSON.parse(selectedApp.documents) : selectedApp.documents;
                            return Object.entries(docs).filter(([, v]) => v).map(([key, value]) => {
                              const isImage = typeof value === 'string' && (value.startsWith('data:image') || (value.startsWith('http') && /\.(jpg|jpeg|png|webp)/i.test(value)));
                              const isPdf = typeof value === 'string' && (value.startsWith('data:application/pdf') || (value.startsWith('http') && /\.pdf/i.test(value)));
                              const isUrl = typeof value === 'string' && value.startsWith('http');
                              const ext = isImage ? (value.match(/\.(jpg|jpeg|png|webp)/i)?.[1] || 'jpg') : 'pdf';
                              const fileName = `${selectedApp.first_name}_${selectedApp.last_name}_${key}.${ext}`;

                              return (
                                <Grid item xs={12} sm={6} key={key}>
                                  <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: P.green, flexShrink: 0 }} />
                                        <Typography variant="caption" fontWeight={700} sx={{ color: P.text }}>{DOC_LABELS[key] || key}</Typography>
                                      </Box>
                                      {isUrl && (
                                        <Box display="flex" gap={0.5}>
                                          <Tooltip title="Ouvrir">
                                            <IconButton size="small" href={value} target="_blank" rel="noopener noreferrer" component="a"
                                              sx={{ color: P.blue, '&:hover': { bgcolor: 'rgba(59,130,246,0.1)' } }}>
                                              <OpenInNewIcon sx={{ fontSize: 15 }} />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Télécharger">
                                            <IconButton size="small" onClick={() => downloadFile(value, fileName)}
                                              sx={{ color: P.green, '&:hover': { bgcolor: 'rgba(34,197,94,0.1)' } }}>
                                              <DownloadIcon sx={{ fontSize: 15 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                    </Box>
                                    {isImage ? (
                                      <Box component="a" href={value} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
                                        <Box component="img" src={value} alt={key}
                                          sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.05)', cursor: 'pointer' }} />
                                      </Box>
                                    ) : isPdf ? (
                                      <Box display="flex" gap={1} mt={0.5}>
                                        <Button size="small" variant="outlined" href={value} target="_blank" rel="noopener noreferrer"
                                          startIcon={<OpenInNewIcon sx={{ fontSize: '12px !important' }} />}
                                          sx={{ color: P.green, borderColor: 'rgba(34,197,94,0.3)', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
                                            '&:hover': { bgcolor: 'rgba(34,197,94,0.08)' } }}>
                                          Voir
                                        </Button>
                                        <Button size="small" variant="outlined" onClick={() => downloadFile(value, fileName)}
                                          startIcon={<DownloadIcon sx={{ fontSize: '12px !important' }} />}
                                          sx={{ color: P.blue, borderColor: 'rgba(59,130,246,0.3)', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
                                            '&:hover': { bgcolor: 'rgba(59,130,246,0.08)' } }}>
                                          Télécharger
                                        </Button>
                                      </Box>
                                    ) : isUrl ? (
                                      <Box display="flex" gap={1} mt={0.5}>
                                        <Button size="small" variant="outlined" href={value} target="_blank" rel="noopener noreferrer"
                                          startIcon={<OpenInNewIcon sx={{ fontSize: '12px !important' }} />}
                                          sx={{ color: P.blue, borderColor: 'rgba(59,130,246,0.3)', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
                                            '&:hover': { bgcolor: 'rgba(59,130,246,0.08)' } }}>
                                          Voir
                                        </Button>
                                        <Button size="small" variant="outlined" onClick={() => downloadFile(value, fileName)}
                                          startIcon={<DownloadIcon sx={{ fontSize: '12px !important' }} />}
                                          sx={{ color: P.green, borderColor: 'rgba(34,197,94,0.3)', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
                                            '&:hover': { bgcolor: 'rgba(34,197,94,0.08)' } }}>
                                          Télécharger
                                        </Button>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: P.textSoft, fontSize: '0.78rem' }}>
                                        Document fourni
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              );
                            });
                          })()}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Raison du rejet (si déjà rejeté) */}
                  {isRejected && selectedApp.review_note && (
                    <Grid item xs={12}>
                      <Alert severity="error" sx={{ borderRadius: '12px', bgcolor: 'rgba(239,68,68,0.08)', color: P.red, border: '1px solid rgba(239,68,68,0.2)' }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Raison du rejet</Typography>
                        <Typography variant="body2">{selectedApp.review_note}</Typography>
                      </Alert>
                    </Grid>
                  )}

                  {/* Champ rejet (si en attente) */}
                  {isPending && (
                    <Grid item xs={12}>
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2 }} />
                      <Typography variant="body2" fontWeight={600} sx={{ color: P.textSoft, mb: 1 }}>
                        Raison du rejet <Box component="span" sx={{ color: P.textMuted, fontWeight: 400 }}>(obligatoire pour rejeter)</Box>
                      </Typography>
                      <TextField fullWidth multiline rows={3} value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Expliquez pourquoi cette demande est rejetée…"
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '12px', color: P.text,
                            '& fieldset': { borderColor: P.cardBorder }, '&:hover fieldset': { borderColor: P.red }, '&.Mui-focused fieldset': { borderColor: P.red } },
                        }} />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={() => setSelectedApp(null)} sx={{ color: P.textMuted, textTransform: 'none', borderRadius: '10px' }}>
                  Fermer
                </Button>
                {isPending && (
                  <>
                    <Button
                      onClick={() => handleReject(selectedApp.id)}
                      disabled={isLoading}
                      variant="outlined"
                      startIcon={isLoading ? <CircularProgress size={16} /> : <CancelIcon />}
                      sx={{ borderColor: P.red, color: P.red, textTransform: 'none', borderRadius: '10px', fontWeight: 700,
                        '&:hover': { bgcolor: 'rgba(239,68,68,0.08)', borderColor: P.red } }}>
                      Rejeter
                    </Button>
                    <Button
                      onClick={() => handleApprove(selectedApp.id)}
                      disabled={isLoading}
                      variant="contained"
                      startIcon={isLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ThumbUpIcon />}
                      sx={{ background: `linear-gradient(135deg, #15803d, ${P.green})`, textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3,
                        '&:hover': { background: `linear-gradient(135deg, #166534, #16a34a)` } }}>
                      Approuver
                    </Button>
                  </>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}

